import setFocusedRowKey from "./mutators/setFocusedRowKey";
import setMultiSelectionAnchorRowKey from "./mutators/setMultiSelectionAnchorRowKey";
import setSelectionAnchorRowKey from "./mutators/setSelectionAnchorRowKey";
import resetSelectionInternal from "./resetSelectionInternal";
import { setSelectionOnRow } from "owa-mail-actions/lib/setSelectionOnRow";
import type { TableView } from "owa-mail-list-store";
import { MailRowDataPropertyGetter } from "owa-mail-list-store";
import getInstrumentationContextsFromTableView from "owa-mail-list-store/lib/utils/getInstrumentationContextsFromTableView";
import MailListItemSelectionSource from "owa-mail-store/lib/store/schema/MailListItemSelectionSource";
import { openTableRowInPopout } from "owa-popout-utils";
import type ReadingPanePopoutItemFolderInfo from "owa-popout-utils/lib/schema/ReadingPanePopoutItemFolderInfo";
import { preparePopoutDataForReadingPane } from "owa-mail-folder-common";
import endSelectMailItemDatapoint from "owa-mail-logging/lib/utils/endSelectMailItemDatapoint";
import { DatapointStatus, logGreyError, logUsage, type PerformanceCoreDatapoint, type PerformanceDatapoint } from "owa-analytics";
import shouldOpenPopout from "../utils/shouldOpenPopout";
import shouldOpenReadingPaneInSingleLineView from "../utils/shouldOpenReadingPaneInSingleLineView";
import { lazyCreateOpenEventUnifiedEventFromMeetingRequest } from "owa-calendar-unified-telemetry-utils";
import { flattenTelemetryData } from "owa-calendar-unified-telemetry-utils/lib/utils/flattenTelemetryData";
import { type UTCalendarOrigin, type UTCalendarUIContext } from "@outlook/unified_telemetry";
import getMeetingItem from "owa-meeting-message/lib/utils/getMeetingItem";
import { isMeetingRequest } from "owa-meeting-message-utils";
import type MeetingRequestMessageType from "owa-service/lib/contract/MeetingRequestMessageType";
import { getMailboxInfo } from "owa-mail-mailboxinfo";

// Bug 32656: In an orchestrator, the render is triggered several times
// Due to this bug, ReadingPane.render() is triggered several times inside this method.
// To solve the reading pane perf issue, wrap this function as an action.
// When the bug is resolve, please remove the action wrapper.

/**
 * Single select mail row
 * @param tableView where the operation is being performed
 * @param rowKey the rowKey of the item to select
 * @param mailListItemSelectionSource The source of selection on mail item
 */
export default function singleSelectRowInternal(tableView: TableView, rowKey: string, mailListItemSelectionSource: MailListItemSelectionSource, eventTimestamp?: number, selectMailItemDatapoint?: PerformanceCoreDatapoint | PerformanceDatapoint): void {
    if (shouldOpenPopout(mailListItemSelectionSource)) {
        const rowKeyValue = mailListItemSelectionSource ===
            MailListItemSelectionSource.MailListItemSimulatedDoubleClick
            ? rowKey
            : [...tableView.selectedRowKeys.keys()][0];
        const rowId = MailRowDataPropertyGetter.getRowClientItemId(rowKeyValue, tableView);
        if (!rowId?.Id?.trim()) {
            endSelectMailItemDatapoint("Error", false /* shouldInvalidate */, DatapointStatus.ClientError);
            return;
        }
        const instrumentationContext = getInstrumentationContextsFromTableView([rowKeyValue], tableView)[0];
        openTableRowInPopout(tableView.tableQuery.listViewType, rowId, async () => {
            const folderInfo = preparePopoutDataForReadingPane(tableView);
            return {
                ...folderInfo,
                instrumentationContext,
            } as ReadingPanePopoutItemFolderInfo;
        }, eventTimestamp, rowKeyValue, 
        // getReadingPaneNavigationContext invokes this provider only when QAT is enabled.
        () => ({
            rowKey: rowKeyValue,
            tableViewId: tableView.id,
        }), selectMailItemDatapoint);
        logOpenEventPopoutFromMeetingRequest(rowKeyValue, tableView, mailListItemSelectionSource, instrumentationContext?.dp);
        
        // For simulated double click, don't return early so that the selection
        // logic kicks in and allows the auto mark as read behavior to work.
        if (mailListItemSelectionSource !==
            MailListItemSelectionSource.MailListItemSimulatedDoubleClick) {
            return;
        }
    }
    
    const isSingleRowSelected = tableView.selectedRowKeys.has(rowKey) && tableView.selectedRowKeys?.size === 1;
    
    // Simply log and retrun if user is selecting the same item and that item is focused unless
    // user has reading pane setting as hidden (single line view) AND the reading pane should be opened.
    if (isSingleRowSelected &&
        rowKey === tableView.focusedRowKey &&
        !shouldOpenReadingPaneInSingleLineView(mailListItemSelectionSource)) {
        // End the SelectMailItem related datapoint here and invalidate since reading pane
        // is already loaded.
        endSelectMailItemDatapoint("" /* contentType */, true /* shouldInvalidate */);
        return;
    }
    
    // If this rowKey is the only selected row and its twisty is being toggled, there is no need to reset and set anything new.
    if (!(mailListItemSelectionSource == MailListItemSelectionSource.MailListItemTwisty &&
        isSingleRowSelected)) {
        // Reset list view selection
        resetSelectionInternal(tableView);
        setFocusedRowKey(tableView, rowKey);
        
        // Set the selection anchor for possible future range, keyboard, or multi selections
        setSelectionAnchorRowKey(tableView, rowKey);
        setMultiSelectionAnchorRowKey(tableView, null);
        
        // Select new item
        setSelectionOnRow(rowKey, tableView.id, true /* shouldSelect */);
    }
}

function logOpenEventPopoutFromMeetingRequest(rowKey: string, tableView: TableView, selectionSource: MailListItemSelectionSource, datapoint?: PerformanceDatapoint): void {
    const meetingRequest = getMeetingRequestFromRow(rowKey, tableView);
    if (!meetingRequest) {
        return;
    }
    
    const origin = selectionSource === MailListItemSelectionSource.KeyboardShiftEnter
        ? "KeyboardShortcut" : "ListItem";
    
    lazyCreateOpenEventUnifiedEventFromMeetingRequest
        .import()
        .then(createOpenEventUnifiedEventFromMeetingRequest => {
        const unifiedEvent = createOpenEventUnifiedEventFromMeetingRequest(meetingRequest, "MailList", origin, "MailReadFormPopout");
        
        if (datapoint) {
            datapoint.addUnifiedTelemetryData(unifiedEvent);
            return;
        }
        
        const flattenedUnifiedTelemetryData = flattenTelemetryData(unifiedEvent);
        
        logUsage("MailListPeekPopoutMeetingRequest", getMailboxInfo(tableView), flattenedUnifiedTelemetryData as any, {
            unifiedTelemetry: unifiedEvent,
        });
    })
        .catch(error => {
        logGreyError("MailOpenEventUnifiedTelemetryFailed", getMailboxInfo(tableView), error, {
            source: "SingleSelectRowInternal",
            tableType: tableView.tableQuery.type,
            listViewType: tableView.tableQuery.listViewType,
            selectionSource,
            hasDatapoint: !!datapoint,
        });
    });
}

function getMeetingRequestFromRow(rowKey: string, tableView: TableView): MeetingRequestMessageType | null {
    const itemIds = MailRowDataPropertyGetter.getItemIds(rowKey, tableView);
    if (!itemIds || itemIds.length === 0) {
        return null;
    }
    
    for (const itemId of itemIds) {
        const { meetingItem } = getMeetingItem(itemId);
        if (meetingItem && isMeetingRequest(meetingItem.ItemClass)) {
            return meetingItem;
        }
    }
    
    return null;
}
